/**
 * V2 of the AddEditConfigurationComponent
 * This component is controlled by the EnableAiTrustLayerByoLlmConfigurationsV2 feature flag.
 * Modify this file to implement the new UI design.
 */
import {
    SpacingToken,
    UiStack,
} from '@experiences/ui-common';
import { useNavigateWithParams } from '@experiences/util';
import { ApButton } from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
    useParams,
    useSearchParams,
} from 'react-router';

import { routePaths } from '../../../common/constants/routePaths';
import { useOrganizationName } from '../../../common/hooks/useOrganizationName';
import type { ByoLlmConfiguration } from '../../../services/llm-gateway/LlmGateway.types';
import { LlmConnectorType } from '../../../services/llm-gateway/LlmGateway.types';
import { resolvePrepopulatedProductFeature } from '../../../services/llm-gateway/LlmGateway.utils';
import { accountGlobalId } from '../../../store/selectors';
import UiForm from '../../common/UiForm';
import UiPageContainer from '../../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../../organizationsettings/AdminBreadCrumbs';
import { useAddEditConfigurationViewModelV2 } from './AddEditConfigurationViewModelV2';
import { ByoConfigurationFormContent } from './subcomponentsV2/ByoConfigurationFormContent';
import { useByoConfigurationForm } from './subcomponentsV2/useByoConfigurationForm';

export { detectModelOverlapErrors } from './subcomponentsV2/useByoConfigurationForm';

export const AddEditConfigurationComponentV2: React.FC = () => {
    const organizationId = useSelector(accountGlobalId);
    const {
        tenantId, folderId, id: configurationId,
    } = useParams() as { tenantId: string; folderId: string; id: string };
    const [ searchParams ] = useSearchParams();
    const productParam = searchParams.get('product') ?? undefined;
    const featureParam = searchParams.get('feature') ?? undefined;
    const llmConfigurationsReturnUrl = useMemo(() => `${routePaths.adminAiTrustLayerLlmConfigurations}?tenantId=${tenantId}&folderId=${folderId}`, [ folderId, tenantId ]);
    const { formatMessage: translate } = useIntl();
    const navigate = useNavigateWithParams();
    const organizationName = useOrganizationName();

    const {
        tenant,
        folder: routeFolder,
        editedProductLlmConfiguration,
        filteredProductConfigurations: productConfigurations,
        llmToConnectorTypeMapping,
        vendorLlmApiFlavors,
        configuredLlms,
        availableLlmsForEditedProductLlmConfiguration,
        integrationServiceConnections,
        isViewLoading: areDependenciesLoading,
    } = useAddEditConfigurationViewModelV2(organizationId, tenantId, folderId, configurationId, productParam, featureParam);

    const breadCrumbLinks = useMemo(() => [
        {
            link: routePaths.admin,
            name: organizationName,
        },
        {
            link: routePaths.adminAiTrustLayer,
            name: translate({ id: 'CLIENT_AI_TRUST_LAYER' }),
        },
        {
            link: llmConfigurationsReturnUrl,
            name: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_TAB' }),
        },
        editedProductLlmConfiguration ?
            {
                link: routePaths.adminAiTrustLayerLlmConfigurationsEdit.replace(':id', configurationId),
                name: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_EDIT_CONFIGURATION' }) + `: ${editedProductLlmConfiguration.name}`,
            } :
            {
                link: routePaths.adminAiTrustLayerLlmConfigurationsAdd,
                name: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_ADD_CONFIGURATION' }),
            },
    ], [ configurationId, editedProductLlmConfiguration, llmConfigurationsReturnUrl, organizationName, translate ]);

    const [ isFormDataLoading, setIsFormDataLoading ] = useState<boolean>();

    const {
        control,
        handleSubmit,
        resetField,
        setValue,
        setError,
        errors,
        isValid,
        isSubmitting,
        folderKey,
        product,
        feature,
        featureHasAllModelsConfigurationConstraint,
        isFormComplete,
        isConfigurationTestingCommencing,
        isConfigurationTested,
        handleFolderChange,
        notifyFieldChanges,
        onTest,
        onSubmit,
        validateLlmConfigurationsModelNameOverlap,
        validateLlmConfigurationsRequiredFields,
    } = useByoConfigurationForm({
        organizationId,
        tenantId,
        configurationId,
        productConfigurations,
        llmToConnectorTypeMapping,
        availableLlms: availableLlmsForEditedProductLlmConfiguration,
        configuredLlms,
        editedProductLlmConfiguration,
        onSuccess: () => navigate(llmConfigurationsReturnUrl),
    });

    // Pre-populate folder from route
    useEffect(() => {
        if (routeFolder) {
            setValue('folderKey', routeFolder.Key);
        }
    }, [ configurationId, routeFolder, setValue ]);

    // Pre-populate form data in edit mode
    useEffect(() => {
        if (routeFolder && editedProductLlmConfiguration && llmToConnectorTypeMapping && productConfigurations && integrationServiceConnections) {
            setIsFormDataLoading(true);
            setValue('folderKey', routeFolder.Key);

            if (configurationId) {
                setValue('product', editedProductLlmConfiguration.product);
                setValue('feature', editedProductLlmConfiguration.operationGroupName);

                setValue('llmConfigurations', editedProductLlmConfiguration.llmConfigurations.reduce((acc, llmConfig) => {
                    const mapKey = llmConfig.defaultModel ?? llmConfig.desiredModel;
                    const formConfig = { ...llmConfig };

                    if (formConfig.connectorType === LlmConnectorType.Custom && formConfig.connectionId) {
                        const connection = integrationServiceConnections?.find(c => c.id === formConfig.connectionId);
                        if (connection?.connector?.key) {
                            formConfig.selfServeByoConfigParameters = { connectorTemplate: connection.connector.key };
                            formConfig.connectorTags = connection.connector.tags;
                        }
                    }

                    formConfig.isCustomAlias = !Object.keys(llmToConnectorTypeMapping).includes(llmConfig.desiredModel);

                    acc[mapKey] = formConfig;
                    return acc;
                }, {} as Record<string, Partial<ByoLlmConfiguration>>));
            }

            setIsFormDataLoading(false);
        }
    }, [
        areDependenciesLoading,
        configurationId,
        editedProductLlmConfiguration,
        integrationServiceConnections,
        llmToConnectorTypeMapping,
        productConfigurations,
        routeFolder,
        setValue,
    ]);

    useEffect(() => {
        const {
            product: prepopulatedProduct, feature: prepopulatedFeature,
        } = resolvePrepopulatedProductFeature(productParam, featureParam, productConfigurations, configurationId);
        if (prepopulatedProduct) {
            setValue('product', prepopulatedProduct);
        }
        if (prepopulatedFeature) {
            setValue('feature', prepopulatedFeature);
        }
    }, [
        configurationId,
        featureParam,
        productConfigurations,
        productParam,
        setValue,
    ]);

    const onClose = useCallback(() => {
        navigate(llmConfigurationsReturnUrl);
    }, [ llmConfigurationsReturnUrl, navigate ]);

    return (
        <UiPageContainer
            loading={areDependenciesLoading || isFormDataLoading}
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={breadCrumbLinks} />}
            position='left'>
            <UiForm
                onSubmit={handleSubmit(onSubmit)}
                addScrollPadding
                actions={
                    <UiStack
                        direction="row"
                        justify="end"
                        gap={SpacingToken.S}>
                        <ApButton
                            label={translate({ id: 'CLIENT_BYO_LLM_CONFIGURE_TEST' })}
                            variant='primary'
                            disabled={!isFormComplete || !isValid || Object.keys(errors).length > 0 || isConfigurationTested}
                            loading={isConfigurationTestingCommencing}
                            type="button"
                            onClick={onTest}
                            data-cy="add-edit-llm-configuration-test-button" />
                        <ApButton
                            label={translate({ id: 'CLIENT_CANCEL' })}
                            variant='secondary'
                            onClick={onClose}
                            data-cy="add-edit-llm-configuration-cancel-button" />
                        <ApButton
                            label={translate({ id: 'CLIENT_SAVE' })}
                            variant='primary'
                            disabled={!isConfigurationTested}
                            loading={isSubmitting}
                            type="submit"
                            data-cy="add-edit-llm-configuration-save-button" />
                    </UiStack>
                }>
                <ByoConfigurationFormContent
                    organizationId={organizationId}
                    control={control}
                    resetField={resetField}
                    setValue={setValue}
                    setError={setError}
                    product={product}
                    feature={feature}
                    folderKey={folderKey}
                    featureHasAllModelsConfigurationConstraint={featureHasAllModelsConfigurationConstraint}
                    productConfigurations={productConfigurations}
                    llmToConnectorTypeMapping={llmToConnectorTypeMapping}
                    vendorLlmApiFlavors={vendorLlmApiFlavors}
                    availableLlms={availableLlmsForEditedProductLlmConfiguration}
                    notifyFieldChanges={notifyFieldChanges}
                    onFolderChange={handleFolderChange}
                    validateLlmConfigurationsModelNameOverlap={validateLlmConfigurationsModelNameOverlap}
                    validateLlmConfigurationsRequiredFields={validateLlmConfigurationsRequiredFields}
                    isEditMode={!!configurationId}
                    tenants={tenant ? [ tenant ] : []}
                />
            </UiForm>
        </UiPageContainer>
    );
};
